import React, { useState, useEffect } from 'react';
import { Volume2, Plus, Edit2, Trash2, X } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { useTheme } from '../../../contexts/ThemeContext';
import { api } from '../../../services/api';

const AnnouncementBox = () => {
    const { user, isAdmin } = useAuth();
    const { theme } = useTheme();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        priority: 'normal'
    });

    // Color palettes for announcements (theme-aware)
    const lightColors = [
        'bg-blue-50 border-blue-200 text-blue-800',
        'bg-purple-50 border-purple-200 text-purple-800',
        'bg-green-50 border-green-200 text-green-800',
        'bg-yellow-50 border-yellow-200 text-yellow-800',
        'bg-pink-50 border-pink-200 text-pink-800',
        'bg-indigo-50 border-indigo-200 text-indigo-800',
        'bg-teal-50 border-teal-200 text-teal-800',
        'bg-orange-50 border-orange-200 text-orange-800',
        'bg-cyan-50 border-cyan-200 text-cyan-800',
        'bg-rose-50 border-rose-200 text-rose-800'
    ];

    const darkColors = [
        'bg-blue-900/30 border-blue-700 text-blue-300',
        'bg-purple-900/30 border-purple-700 text-purple-300',
        'bg-green-900/30 border-green-700 text-green-300',
        'bg-yellow-900/30 border-yellow-700 text-yellow-300',
        'bg-pink-900/30 border-pink-700 text-pink-300',
        'bg-indigo-900/30 border-indigo-700 text-indigo-300',
        'bg-teal-900/30 border-teal-700 text-teal-300',
        'bg-orange-900/30 border-orange-700 text-orange-300',
        'bg-cyan-900/30 border-cyan-700 text-cyan-300',
        'bg-rose-900/30 border-rose-700 text-rose-300'
    ];

    const announcementColors = theme === 'dark' ? darkColors : lightColors;

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const fetchAnnouncements = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/announcements', {
                params: { limit: 10 }
            });
            setAnnouncements(response.data.announcements || []);
        } catch (error) {
            console.error('Error fetching announcements:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                ...formData,
                targetAudience: formData.message.includes('@everyone') ? 'all' : 'all'
            };

            if (editingId) {
                // Update existing
                await api.put(`/api/announcements/${editingId}`, payload);
            } else {
                // Create new
                await api.post('/api/announcements', payload);
            }

            setShowModal(false);
            setFormData({ title: '', message: '', priority: 'normal' });
            setEditingId(null);
            fetchAnnouncements();
        } catch (error) {
            console.error('Error saving announcement:', error);
            alert('Failed to save announcement');
        }
    };

    const handleEdit = (announcement) => {
        setFormData({
            title: announcement.title,
            message: announcement.message,
            priority: announcement.priority || 'normal'
        });
        setEditingId(announcement.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) {
            return;
        }

        try {
            await api.delete(`/api/announcements/${id}`);
            fetchAnnouncements();
        } catch (error) {
            console.error('Error deleting announcement:', error);
            alert('Failed to delete announcement');
        }
    };

    const handleInsertMention = () => {
        setFormData(prev => ({
            ...prev,
            message: prev.message + ' @everyone'
        }));
    };

    return (
        <div className={`rounded-lg shadow-sm border p-4 mb-4 ${
            theme === 'dark'
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
        }`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-orange-100 rounded-lg">
                        <Volume2 className="w-5 h-5 text-orange-600" />
                    </div>
                    <h3 className={`font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-800'
                    }`}>Announcements</h3>
                </div>
                {isAdmin && (
                    <button
                        onClick={() => {
                            setFormData({ title: '', message: '', priority: 'normal' });
                            setEditingId(null);
                            setShowModal(true);
                        }}
                        className={`p-1.5 rounded-lg transition-colors ${
                            theme === 'dark'
                                ? 'hover:bg-gray-700 text-gray-300'
                                : 'hover:bg-gray-100 text-gray-600'
                        }`}
                        title="Add Announcement"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Announcements List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
                {loading ? (
                    <div className={`text-center py-8 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                        Loading announcements...
                    </div>
                ) : announcements.length === 0 ? (
                    <div className={`text-center py-8 ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                        <Volume2 className={`w-12 h-12 mx-auto mb-2 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                        }`} />
                        <p className="text-sm">Currently there are no active announcements</p>
                        {isAdmin && (
                            <p className="text-xs mt-1">Click the + button to create one</p>
                        )}
                    </div>
                ) : (
                    announcements.map((announcement, index) => (
                        <div
                            key={announcement.id}
                            className={`p-3 rounded-lg border-l-4 ${announcementColors[index % announcementColors.length]} transition-all`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-sm mb-1 flex items-center gap-2">
                                        {announcement.title}
                                        {announcement.priority === 'high' && (
                                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                                                theme === 'dark'
                                                    ? 'bg-red-900/40 text-red-300'
                                                    : 'bg-red-100 text-red-700'
                                            }`}>
                                                Important
                                            </span>
                                        )}
                                    </h4>
                                    <p className="text-xs break-words whitespace-pre-wrap">
                                        {announcement.message}
                                    </p>
                                    <p className="text-xs opacity-60 mt-2">
                                        {announcement.created_by_name || 'Admin'} • {new Date(announcement.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}
                                    </p>
                                </div>
                                {isAdmin && (
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => handleEdit(announcement)}
                                            className={`p-1 rounded transition-colors ${
                                                theme === 'dark'
                                                    ? 'hover:bg-gray-700/50'
                                                    : 'hover:bg-white/50'
                                            }`}
                                            title="Edit"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(announcement.id)}
                                            className={`p-1 rounded transition-colors ${
                                                theme === 'dark'
                                                    ? 'hover:bg-gray-700/50'
                                                    : 'hover:bg-white/50'
                                            }`}
                                            title="Delete"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className={`rounded-lg shadow-xl w-full max-w-md ${
                        theme === 'dark'
                            ? 'bg-gray-800 border border-gray-700'
                            : 'bg-white'
                    }`}>
                        <div className={`flex items-center justify-between p-4 border-b ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                            <h3 className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-800'
                            }`}>
                                {editingId ? 'Edit Announcement' : 'New Announcement'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowModal(false);
                                    setFormData({ title: '', message: '', priority: 'normal' });
                                    setEditingId(null);
                                }}
                                className={`p-1 rounded ${
                                    theme === 'dark'
                                        ? 'hover:bg-gray-700 text-gray-300'
                                        : 'hover:bg-gray-100 text-gray-600'
                                }`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-4 space-y-4">
                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Title *
                                </label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    placeholder="e.g., Holiday Notice"
                                    required
                                />
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Message *
                                </label>
                                <textarea
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                    rows="4"
                                    placeholder="Enter your announcement message..."
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={handleInsertMention}
                                    className="mt-2 text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                                >
                                    <span className={`font-mono px-2 py-1 rounded ${
                                        theme === 'dark' ? 'bg-orange-900/30' : 'bg-orange-50'
                                    }`}>@everyone</span>
                                    Click to mention everyone
                                </button>
                            </div>

                            <div>
                                <label className={`block text-sm font-medium mb-1 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Priority
                                </label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white'
                                            : 'bg-white border-gray-300 text-gray-900'
                                    }`}
                                >
                                    <option value="normal">Normal</option>
                                    <option value="high">High (Important)</option>
                                </select>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowModal(false);
                                        setFormData({ title: '', message: '', priority: 'normal' });
                                        setEditingId(null);
                                    }}
                                    className={`flex-1 px-4 py-2 border rounded-lg transition-colors ${
                                        theme === 'dark'
                                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                                >
                                    {editingId ? 'Update' : 'Post'} Announcement
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnnouncementBox;
